import cn from 'classnames';
import { forwardRef } from 'react';

import { ArrowDownIcon, ArrowUpIcon } from '../../../foundations/icon';

import type { ForwardedRef, KeyboardEvent, ReactElement } from 'react';

import type { TableSortLabelProps } from './table-sort-label.types';

import './table-sort-label.scss';

/**
 * A button based label for placing inside `TableCell` for column sorting.
 * It can be used to indicate the sorted column and the sorting direction.
 *
 * ### Usage
 *
 * ```tsx
 * import { TableHead, TableRow, TableCell, TableSortLabel } from '@bloomreach/react-banana-ui';
 *
 * export default function MySortableTableHeader() {
 *   return (
 *     <TableHead>
 *       <TableRow>
 *         <TableCell><TableSortLabel direction="desc">Name</TableSortLabel></TableCell>
 *         <TableCell><TableSortLabel direction="asc">Email</TableSortLabel></TableCell>
 *       </TableRow>
 *    </TableHead>
 *   );
 * }
 * ```
 */
const TableSortLabel = forwardRef(
  (props: TableSortLabelProps, forwardedRef: ForwardedRef<HTMLSpanElement> | null): ReactElement => {
    const {
      children,
      className,
      direction = false,
      onClick,
      ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
    } = props;

    const ArrowIcon = direction === 'desc' ? ArrowDownIcon : ArrowUpIcon;

    const handleKeyDown = (event: KeyboardEvent<HTMLSpanElement>): void => {
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();

        onClick?.(event);
      }
    };

    return (
      <span
        className={cn('rbui-table-sort-label', { 'rbui-table-sort-label--active': direction }, className)}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        ref={forwardedRef}
        role="button" // TODO: Replace with button element without styles of use a new inline button component, see BR-170
        tabIndex={0}
        {...rest}
      >
        {children}
        <ArrowIcon className="rbui-table-sort-label__icon" />
      </span>
    );
  },
);

TableSortLabel.displayName = 'TableSortLabel';

export default TableSortLabel;
